import React, { useState, useEffect } from 'react';

const TeamStatistics = ({ teams, asymmetricResults }) => {
  const [teamStats, setTeamStats] = useState([]);
  const [overallStats, setOverallStats] = useState({});
  const [selectedTeam, setSelectedTeam] = useState(null);

  // チーム別統計を計算
  useEffect(() => {
    if (teams.length === 0) {
      setTeamStats([]);
      return;
    }

    const stats = teams.map(team => {
      // 非対称戦の成績
      const teamResults = asymmetricResults.filter(r => 
        r.hunterTeams?.includes(team.id) || r.survivorTeams?.includes(team.id)
      );

      let totalPoints = 0;
      let hunterWins = 0;
      let hunterLosses = 0;
      let survivorWins = 0;
      let survivorLosses = 0;
      let totalMatches = 0;
      let hunterMatches = 0;
      let survivorMatches = 0;

      teamResults.forEach(result => {
        totalMatches++;
        
        // 前半戦の結果
        if (result.firstHalf) {
          if (result.firstHalf.hunterTeam === team.id) {
            hunterMatches++;
            if (result.firstHalf.winner === 'hunter') {
              hunterWins++;
              totalPoints += 3;
            } else {
              hunterLosses++;
            }
          } else if (result.firstHalf.survivorTeams.includes(team.id)) {
            survivorMatches++;
            if (result.firstHalf.winner === 'survivors') {
              survivorWins++;
              totalPoints += 1;
            } else {
              survivorLosses++;
            }
          }
        }

        // 後半戦の結果
        if (result.secondHalf) {
          if (result.secondHalf.hunterTeam === team.id) {
            hunterMatches++;
            if (result.secondHalf.winner === 'hunter') {
              hunterWins++;
              totalPoints += 3;
            } else {
              hunterLosses++;
            }
          } else if (result.secondHalf.survivorTeams.includes(team.id)) {
            survivorMatches++;
            if (result.secondHalf.winner === 'survivors') {
              survivorWins++;
              totalPoints += 1;
            } else {
              survivorLosses++;
            }
          }
        }
      });

      // 総合成績
      const totalWins = hunterWins + survivorWins;
      const totalLosses = hunterLosses + survivorLosses;
      const totalGames = totalWins + totalLosses;
      const winRate = totalGames > 0 ? (totalWins / totalGames * 100) : 0;
      const hunterWinRate = hunterMatches > 0 ? (hunterWins / hunterMatches * 100) : 0;
      const survivorWinRate = survivorMatches > 0 ? (survivorWins / survivorMatches * 100) : 0;

      return {
        team,
        asymmetric: {
          totalPoints,
          hunterWins,
          hunterLosses,
          survivorWins,
          survivorLosses,
          hunterMatches,
          survivorMatches,
          totalMatches
        },
        overall: {
          totalWins,
          totalLosses,
          totalGames,
          winRate: parseFloat(winRate.toFixed(1)),
          hunterWinRate: parseFloat(hunterWinRate.toFixed(1)),
          survivorWinRate: parseFloat(survivorWinRate.toFixed(1)),
          totalPoints
        }
      };
    });

    // 総ポイント順でソート
    stats.sort((a, b) => {
      if (b.overall.totalPoints !== a.overall.totalPoints) {
        return b.overall.totalPoints - a.overall.totalPoints;
      }
      return b.overall.winRate - a.overall.winRate;
    });

    setTeamStats(stats);
  }, [teams, asymmetricResults]);

  // 全体統計を計算
  useEffect(() => {
    const totalTeams = teams.length;
    const totalAsymmetricMatches = asymmetricResults.length;
    const totalGames = totalAsymmetricMatches * 2; // 前半戦 + 後半戦

    const asymmetricCompleted = totalAsymmetricMatches > 0;

    setOverallStats({
      totalTeams,
      totalAsymmetricMatches,
      totalGames,
      asymmetricCompleted
    });
  }, [teams, asymmetricResults]);

  const getTeamRank = (teamId) => {
    const index = teamStats.findIndex(stat => stat.team.id === teamId);
    return index + 1;
  };

  const formatPercentage = (value) => {
    return `${value}%`;
  };

  if (teams.length === 0) {
    return (
      <div className="team-statistics">
        <div className="section-header">
          <h2>📊 チーム成績・統計</h2>
          <p>チームの成績と大会の統計情報</p>
        </div>
        <div className="empty-state">
          <p>統計を表示するには、チームを登録してください</p>
        </div>
      </div>
    );
  }

  return (
    <div className="team-statistics">
      <div className="section-header">
        <h2>📊 チーム成績・統計</h2>
        <p>チームの成績と大会の統計情報</p>
      </div>

      {/* 大会全体の統計 */}
      <div className="overall-stats-section">
        <h3>🏟️ 大会全体統計</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <div className="stat-value">{overallStats.totalTeams}</div>
              <div className="stat-label">参加チーム数</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚔️</div>
            <div className="stat-info">
              <div className="stat-value">{overallStats.totalAsymmetricMatches}</div>
              <div className="stat-label">1対4戦試合数</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🎮</div>
            <div className="stat-info">
              <div className="stat-value">{overallStats.totalGames}</div>
              <div className="stat-label">総ゲーム数</div>
              <div className="stat-status">
                (前半戦 + 後半戦)
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📈</div>
            <div className="stat-info">
              <div className="stat-value">
                {overallStats.asymmetricCompleted ? '進行中' : '未開始'}
              </div>
              <div className="stat-label">大会状況</div>
              <div className="stat-status">
                {overallStats.asymmetricCompleted ? '✅ 試合実施済み' : '⏳ 試合待ち'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* チーム成績ランキング */}
      <div className="team-rankings-section">
        <h3>🏅 チーム成績ランキング</h3>
        <div className="rankings-table">
          <div className="table-header">
            <div className="rank-col">順位</div>
            <div className="name-col">チーム名</div>
            <div className="points-col">総ポイント</div>
            <div className="hunter-col">ハンター戦績</div>
            <div className="survivor-col">サバイバー戦績</div>
            <div className="winrate-col">総合勝率</div>
            <div className="matches-col">参加試合</div>
          </div>
          {teamStats.map((stat, index) => (
            <div 
              key={stat.team.id} 
              className={`table-row ${selectedTeam === stat.team.id ? 'selected' : ''}`}
              onClick={() => setSelectedTeam(selectedTeam === stat.team.id ? null : stat.team.id)}
            >
              <div className="rank-col">
                <span className={`rank ${index < 3 ? `rank-${index + 1}` : ''}`}>
                  {index + 1}
                  {index === 0 && '🥇'}
                  {index === 1 && '🥈'}
                  {index === 2 && '🥉'}
                </span>
              </div>
              <div className="name-col">{stat.team.name}</div>
              <div className="points-col">{stat.overall.totalPoints}pt</div>
              <div className="hunter-col">
                {stat.asymmetric.hunterWins}勝{stat.asymmetric.hunterLosses}敗
                {stat.asymmetric.hunterMatches > 0 && (
                  <span className="win-rate">({stat.overall.hunterWinRate}%)</span>
                )}
              </div>
              <div className="survivor-col">
                {stat.asymmetric.survivorWins}勝{stat.asymmetric.survivorLosses}敗
                {stat.asymmetric.survivorMatches > 0 && (
                  <span className="win-rate">({stat.overall.survivorWinRate}%)</span>
                )}
              </div>
              <div className="winrate-col">{formatPercentage(stat.overall.winRate)}</div>
              <div className="matches-col">{stat.asymmetric.totalMatches}</div>
            </div>
          ))}
        </div>
      </div>

      {/* チーム詳細情報 */}
      {selectedTeam && (
        <div className="team-detail-section">
          {(() => {
            const stat = teamStats.find(s => s.team.id === selectedTeam);
            if (!stat) return null;
            
            return (
              <div className="team-detail-card">
                <div className="detail-header">
                  <h3>📋 {stat.team.name} の詳細成績</h3>
                  <div className="detail-rank">総合順位: {getTeamRank(selectedTeam)}位</div>
                </div>
                
                <div className="detail-stats-grid">
                  <div className="detail-section">
                    <h4>⚔️ 1対4戦成績</h4>
                    <div className="detail-stats">
                      <div className="detail-stat">
                        <span className="label">参加試合数:</span>
                        <span className="value">{stat.asymmetric.totalMatches}</span>
                      </div>
                      <div className="detail-stat">
                        <span className="label">総ポイント:</span>
                        <span className="value">{stat.overall.totalPoints}pt</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>🎯 ハンター成績</h4>
                    <div className="detail-stats">
                      <div className="detail-stat">
                        <span className="label">ハンター回数:</span>
                        <span className="value">{stat.asymmetric.hunterMatches}</span>
                      </div>
                      <div className="detail-stat">
                        <span className="label">勝利:</span>
                        <span className="value">{stat.asymmetric.hunterWins}</span>
                      </div>
                      <div className="detail-stat">
                        <span className="label">敗北:</span>
                        <span className="value">{stat.asymmetric.hunterLosses}</span>
                      </div>
                      <div className="detail-stat">
                        <span className="label">勝率:</span>
                        <span className="value">{formatPercentage(stat.overall.hunterWinRate)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>🏃 サバイバー成績</h4>
                    <div className="detail-stats">
                      <div className="detail-stat">
                        <span className="label">サバイバー回数:</span>
                        <span className="value">{stat.asymmetric.survivorMatches}</span>
                      </div>
                      <div className="detail-stat">
                        <span className="label">勝利:</span>
                        <span className="value">{stat.asymmetric.survivorWins}</span>
                      </div>
                      <div className="detail-stat">
                        <span className="label">敗北:</span>
                        <span className="value">{stat.asymmetric.survivorLosses}</span>
                      </div>
                      <div className="detail-stat">
                        <span className="label">勝率:</span>
                        <span className="value">{formatPercentage(stat.overall.survivorWinRate)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>📈 総合成績</h4>
                    <div className="detail-stats">
                      <div className="detail-stat">
                        <span className="label">総勝利数:</span>
                        <span className="value">{stat.overall.totalWins}</span>
                      </div>
                      <div className="detail-stat">
                        <span className="label">総敗北数:</span>
                        <span className="value">{stat.overall.totalLosses}</span>
                      </div>
                      <div className="detail-stat">
                        <span className="label">総合勝率:</span>
                        <span className="value">{formatPercentage(stat.overall.winRate)}</span>
                      </div>
                      <div className="detail-stat">
                        <span className="label">順位:</span>
                        <span className="value">{getTeamRank(selectedTeam)}位</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 役割別パフォーマンス */}
                <div className="performance-analysis">
                  <h4>🎭 役割別パフォーマンス分析</h4>
                  <div className="performance-grid">
                    <div className="performance-card hunter-performance">
                      <h5>ハンター適性</h5>
                      <div className="performance-score">
                        {stat.asymmetric.hunterMatches > 0 ? (
                          <>
                            <div className="score-value">{stat.overall.hunterWinRate}%</div>
                            <div className="score-label">
                              {stat.overall.hunterWinRate >= 70 ? '🔥 優秀' :
                               stat.overall.hunterWinRate >= 50 ? '👍 良好' :
                               stat.overall.hunterWinRate >= 30 ? '📈 普通' : '💪 要改善'}
                            </div>
                          </>
                        ) : (
                          <div className="no-data">データなし</div>
                        )}
                      </div>
                    </div>
                    <div className="performance-card survivor-performance">
                      <h5>サバイバー適性</h5>
                      <div className="performance-score">
                        {stat.asymmetric.survivorMatches > 0 ? (
                          <>
                            <div className="score-value">{stat.overall.survivorWinRate}%</div>
                            <div className="score-label">
                              {stat.overall.survivorWinRate >= 70 ? '🔥 優秀' :
                               stat.overall.survivorWinRate >= 50 ? '👍 良好' :
                               stat.overall.survivorWinRate >= 30 ? '📈 普通' : '💪 要改善'}
                            </div>
                          </>
                        ) : (
                          <div className="no-data">データなし</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default TeamStatistics;
